import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

const checkSalesUserRole = (): {
  isSalesUser: boolean;
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isSalesUser = false;
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);

      const tenantId = localStorage.getItem("selectedTenantId");

      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isSalesUser = tenantObj?.role_key === "SALESUSER";
      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn(
        "Pipeline: Could not parse auth response to check for user role",
        e
      );
    }
  }

  return { isSalesUser, isClientAdmin, userId };
};

const getPipelineUrl = () => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

  const { isSalesUser, userId } = checkSalesUserRole();

  let url = `${API_BASE_URL}/pipeline/?tenant_id=${tenantId}`;

  if (isSalesUser && userId) {
    url += `&sales_user_id=${userId}`;
  }

  return url;
};

const getPipelineByIdUrl = (id: number) => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

  const { isSalesUser, userId } = checkSalesUserRole();

  let url = `${API_BASE_URL}/pipeline/${id}/?tenant=${tenantId}`;

  if (isSalesUser && userId) {
    url += `&sales_user_id=${userId}`;
  }

  return url;
};

const addTenantToPipeline = <T extends { tenant_id?: number | null }>(
  pipeline: Partial<T>
): Partial<T> => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) {
    throw new Error("Tenant ID not found in localStorage.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...pipeline,
    tenant_id: parseInt(tenantId, 10),
    created_by: currentUserId,
    updated_by: currentUserId,
  };
};

export interface Pipeline {
  pipeline_id?: number;
  crm_entity_id?: number;
  created_by_username: string;
  updated_by_username: string;
  title: string;
  created_datetime: string;
  updated_datetime: string;
  assigned_datetime: string;
  priority: string;
  products_n_services?: string | null;
  estimated_value?: number | null;
  currency_code?: string | null;
  source?: string;
  lifecycle_source_id?: number | null;
  lifecycle_source?: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
  };
  primary_contact: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    full_name: string;
    avatar_url: string;
    job_title?: string;
  } | null;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  company: {
    company_id: number;
    company_name: string;
    business_type_display_name: string;
    industry_type_display_name: string;
    avatar_url: string;
    address: string;
  } | null;
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    stage_name: string;
  } | null;
  assigned_user: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  } | null;
  tenant: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
    is_active: boolean;
    language: string | null;
  } | null;
  realted_lead: {
    lead_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    lifecycle_source: {
      lifecycle_source_id: number;
      lifecycle_source_key: string;
      lifecycle_source_display_name: string;
    };
    priority: string;
  } | null;
  assigned_team?: {
    team_id: number;
    name?: string;
    team_name?: string;
    manager?: {
      user_id: number;
      full_name: string;
      
      avatar_url: string;
    };
  };
  product_n_service?: {
    lifecycle_product_n_service_id: number;
    product_n_service_name: string;
    product_n_service_unit_price: number;
    currency_code: string;
    product_n_service_type_id: number;
    related_entity_name: string;
    related_entity_id: number;
    quantity: number;
    discount_percentage: number;
    gross_total: number;
    discounted_total: number;
    created_datetime: string;
    updated_datetime: string;
    vat_tax_percentage: number;
    vat_tax_total: number;
    gst_tax_percentage: number;
    gst_tax_total: number;
    service_tax_percentage: number;
    service_tax_total: number;
    tenant_id: number;
    product_n_service_id: number;
    created_by: number;
    updated_by: number | null;
  }[];
  is_archive?: boolean;
   tags?: {
      insert : {entity_tag_id: number; entity_tag_name: string}[];} | null;
  lifecycle_status_reason?: {
    lifecycle_status_reason_id: number;
    reason_name: string;
    lifecycle_status_reason_category_key: string;
  } | null;
  };



export interface PipelineRequest {
  title: string;
  priority: string;
  products_n_services?: string | null;
  estimated_value?: number | null;
  currency_code?: string | null;
  source?: string;
  lifecycle_source_id?: number | null;
  primary_contact?: number | null;
  company_id?: number | null;
  assigned_user_id?: number | null;
  lifecycle_type_stage_id?: number | null;
  related_lead_id?: number | null;
  tenant_id?: number | null;
}

export interface PaginatedPipelineResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Pipeline[];
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pipelines: Pipeline[];
}

export class PipelineService {
  static async getPipelines(): Promise<Pipeline[]> {
    try {
      const response = await axios.get<{ results: Pipeline[] }>(
        `${getPipelineUrl()}&is_archive=false&response_mode=light`,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data.results;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getPipelinesByPage(
    page: number = 1,
    pageSize: number = 20,
    filters?: Record<string, any>
  ): Promise<PaginatedPipelineResponse> {
    try {
      let url = `${getPipelineUrl()}&page=${page}&page_size=${pageSize}`;

      // Handle archived filter
      if (filters && filters.archivedFilter) {
        if (filters.archivedFilter === "yes") {
          url += `&is_archive=true`;
        } else if (filters.archivedFilter === "no") {
          url += `&is_archive=false`;
        }
        // For "both", don't add is_archive parameter
      } else {
        // Default to non-archived if no filter specified
        url += `&is_archive=false`;
      }

      // Add filter parameters if provided
      if (filters) {
        if (filters.searchTerm) {
          url += `&title=${encodeURIComponent(filters.searchTerm)}`;
        }
        if (filters.dateRange && filters.dateRange[0]) {
          url += `&created_datetime_gte=${filters.dateRange[0].toISOString().split('T')[0]}`;
        }
        if (filters.dateRange && filters.dateRange[1]) {
          url += `&created_datetime_lte=${filters.dateRange[1].toISOString().split('T')[0]}`;
        }
        if (filters.statusFilter) {
          url += `&lifecycle_type_stage_id=${encodeURIComponent(filters.statusFilter)}`;
        }

        Object.keys(filters).forEach((key) => {
          if (!["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            const val = filters[key];
            if (val !== undefined && val !== null && val !== "") {
              url += `&${key}=${encodeURIComponent(String(val))}`;
            }
          }
        });
      }

      const response = await axios.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: Pipeline[];
      }>(url, {
        headers: getAuthHeaders(),
      });

      const totalCount = response.data.count;
      const totalPages = Math.ceil(totalCount / pageSize);
      const hasMore = response.data.next !== null;

      return {
        ...response.data,
        totalPages,
        totalCount,
        hasMore,
        pipelines: response.data.results,
      };
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getPipelineById(id: number): Promise<Pipeline> {
    try {
      const url = getPipelineByIdUrl(id);
      const response = await axios.get<Pipeline>(url, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }
  static async createPipeline(
    pipeline: Partial<PipelineRequest>
  ): Promise<Pipeline> {
    try {
      const pipelineWithTenant = addTenantToPipeline(pipeline);

      const response = await axios.post<Pipeline>(
        getPipelineUrl(),
        pipelineWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async updatePipeline(
    id: number,
    pipeline: Partial<PipelineRequest>
  ): Promise<Pipeline> {
    try {
      const pipelineWithTenant = addTenantToPipeline(pipeline);

      const response = await axios.patch<Pipeline>(
        getPipelineByIdUrl(id),
        pipelineWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }
  static async deletePipeline(id: number): Promise<void> {
    try {
      await axios.delete(getPipelineByIdUrl(id), {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting pipeline:", error);
      handleAuthError(error);
      throw error;
    }
  }

  static async updatePipelineStage(
    pipelineId: number,
    stageId: number
  ): Promise<Pipeline> {
    try {
      const response = await axios.patch<Pipeline>(
        getPipelineByIdUrl(pipelineId),
        { lifecycle_type_stage_id: stageId },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async updatePipelinePriority(
    pipelineId: number,
    priority: string
  ): Promise<Pipeline> {
    try {
      const response = await axios.patch<Pipeline>(
        getPipelineByIdUrl(pipelineId),
        { priority },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getCrmEntityId(): Promise<number | undefined> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-entity/?crm_entity_key=PIPELINE`,
        { headers: getAuthHeaders() }
      );
      if (response.data?.results?.length > 0) {
        return response.data.results[0].crm_entity_id;
      }
    } catch (error) {
      console.error("Error fetching CRM entity ID:", error);
      return undefined;
    }
  }

  static async getPipelineLookup(id: number, tenantId: number): Promise<any> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/lookup/pipeline/?pipeline_id=${id}&tenant_id=${tenantId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pipeline lookup:", error);
      throw error;
    }
  }
}
